import { useCallback, useState, useEffect } from "react";

export const loadLocalStorageState = <T>(key: string, initialValue: T): T => {
  const savedValue = localStorage.getItem(key);

  if (!savedValue) {
    localStorage.setItem(key, JSON.stringify(initialValue));
  }

  return savedValue ? JSON.parse(savedValue) : initialValue;
}

export const setLocalStorageState = <T>(key: string, value: T) => {
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Helper function to store a local value in local storage
 * and sync it with other tabs/windows/sessions.
 * This uses an event based system to ensure all usages of the value
 * are in sync, creating a 'real-time' effect.
 * Note that I have previously implemented this for a personal
 * project and reused that here.
 * @param key
 * @param initialValue
 * @returns
 */
export const useLocalStorageState = <T>(key: string, initialValue: T) => {
  const [value, setValue] = useState<T>(() => {
    return loadLocalStorageState<T>(key, initialValue);
  });

  const setLocalStorageValue = useCallback(
    (newValue: T) => {
      const serialized = JSON.stringify(newValue);
      setLocalStorageState(key, newValue);

      window.dispatchEvent(
        new StorageEvent("storage", {
          key,
          newValue: serialized as unknown as string,
          storageArea: window.localStorage,
        }),
      );
    },
    [key],
  );

  useEffect(() => {
    const listener = (ev: StorageEvent) => {
      if (ev.storageArea === window.localStorage && ev.key === key) {
        setValue(JSON.parse(ev.newValue ?? "") as T);
      }
    };

    window.addEventListener("storage", listener);

    return () => {
      window.removeEventListener("storage", listener);
    };
  }, [key]);

  return [value, setLocalStorageValue] as const;
};
